import { LoadingButton, type UseModalState } from "@clipboard-health/ui-react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { API_TYPES } from "@src/appV2/api";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { InterviewTimePickerField } from "@src/appV2/InterviewSettings/components/InterviewTimePickerField";
import { parseHHmmToMs, validateStartBeforeEnd } from "@src/appV2/InterviewSettings/utils";
import { useToast } from "@src/appV2/lib/Notifications";
import {
  getFetchPaginatedPlacementApplicationsQueryKey,
  type GetPlacementApplicationsForWorkplaceQuery,
} from "@src/appV2/PlacementApplications/api/useFetchPaginatedPlacementApplications";
import { InterviewType } from "@src/appV2/PlacementApplications/types/fetchInterviews.schema";
import { PlacementApplicationStatus } from "@src/appV2/PlacementApplications/types/placementApplicationStatus";
import { useQueryClient } from "@tanstack/react-query";
import { addDays, isAfter, isBefore, subDays } from "date-fns";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import { useCreateInterview } from "../api/useCreateInterview";
import { createInterviewFormDataToTime } from "../utils/create-interview-form-data-to-time.mapper";

const maxDaysIntoFuture = 30;
const SIX_AM_IN_MILLISECONDS = 1000 * 60 * 60 * 6;
const SEVEN_PM_IN_MILLISECONDS = 1000 * 60 * 60 * 19;
const FIFTEEN_MINUTES_IN_MILLISECONDS = 1000 * 60 * 15;

// Schema for the form using zod
const formSchema = z
  .object({
    date: z.date({ required_error: "Please select a date" }),
    interviewTime: z.object({
      startTime: z.string({ required_error: "Please select a start time" }),
      endTime: z.string({ required_error: "Please select an end time" }),
    }),
    acknowledgeCandidateAvailability: z.boolean({
      required_error:
        "Please acknowledge that you are scheduling the interview based on the times agreed upon by the candidate",
    }),
    bypassPlacementDocumentRequirementCheck: z.boolean().optional(),
  })
  .refine(
    (data) => {
      const today = new Date();
      const maxDate = addDays(today, maxDaysIntoFuture);
      return isAfter(data.date, subDays(today, 1)) && isBefore(data.date, maxDate);
    },
    {
      message: `Date must be within the next ${maxDaysIntoFuture} days`,
      path: ["date"],
    }
  )
  .refine((data) => data.acknowledgeCandidateAvailability, {
    message: "Please acknowledge before scheduling the interview",
    path: ["acknowledgeCandidateAvailability"],
  })
  .refine(
    ({ interviewTime }) => {
      const startTime = parseHHmmToMs(interviewTime.startTime);
      const endTime = parseHHmmToMs(interviewTime.endTime);
      return startTime >= SIX_AM_IN_MILLISECONDS && endTime <= SEVEN_PM_IN_MILLISECONDS;
    },
    {
      message: "Start time must be after 6:00 AM and end time must be before 7:00 PM",
      path: ["interviewTime"],
    }
  )
  .refine(
    ({ interviewTime }) =>
      validateStartBeforeEnd({ startTime: interviewTime.startTime, endTime: interviewTime.endTime })
        .isValid,
    {
      message: "Start time must be before end time",
      path: ["interviewTime"],
    }
  )
  .refine(
    ({ interviewTime }) => {
      const startTime = parseHHmmToMs(interviewTime.startTime);
      const endTime = parseHHmmToMs(interviewTime.endTime);
      return endTime - startTime >= FIFTEEN_MINUTES_IN_MILLISECONDS;
    },
    {
      message: "Interview duration must be at least 15 minutes",
      path: ["interviewTime"],
    }
  );

export type WorkplaceCreateInterviewForm = z.infer<typeof formSchema>;

interface WorkplaceCreateInterviewProps {
  placementApplicationId: string;
  workplaceId: string;
  workerId: string;
  modalState: UseModalState;
  onInterviewCreated?: () => void;
}

export function WorkplaceCreateInterview(props: WorkplaceCreateInterviewProps) {
  const { placementApplicationId, modalState, onInterviewCreated, workerId, workplaceId } = props;
  const { profile } = useSession();
  const { showSuccessToast, showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const queryParams: GetPlacementApplicationsForWorkplaceQuery = {
    filter: {
      "worker.id": workerId,
      status: [
        PlacementApplicationStatus.APPLIED,
        PlacementApplicationStatus.ACCEPTED,
        PlacementApplicationStatus.INTERVIEW,
        PlacementApplicationStatus.INTERVIEW_HELD,
        PlacementApplicationStatus.TURNED_AWAY,
        PlacementApplicationStatus.OFFER,
        PlacementApplicationStatus.HIRED,
        PlacementApplicationStatus.ONBOARDING,
        PlacementApplicationStatus.RETAINED,
        PlacementApplicationStatus.RESIGNED,
        PlacementApplicationStatus.REJECTED,
        PlacementApplicationStatus.OFFER_REJECTED,
      ].join(","),
      "workplace.id": workplaceId,
    },
    page: {
      size: 20,
    },
  };

  // React Hook Form setup
  const methods = useForm<WorkplaceCreateInterviewForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date: new Date(), // default today
      interviewTime: {
        startTime: "06:00",
        endTime: "07:00",
      },
      acknowledgeCandidateAvailability: false,
    },
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
  } = methods;

  // API mutation hook
  const { mutateAsync: createInterview, isLoading: isCreating } = useCreateInterview({
    onSuccess: () => {
      showSuccessToast("Interview scheduled successfully");
      modalState.closeModal();
      onInterviewCreated?.();
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data.errors?.[0]?.detail ?? "Unable to schedule interview";
      showErrorToast(errorMessage);
    },
  });

  // Form submit
  const onSubmit = async (data: WorkplaceCreateInterviewForm) => {
    const { startTime, endTime } = createInterviewFormDataToTime(
      data,
      profile?.tmz ?? "America/New_York"
    );

    await createInterview({
      relatedResource: { type: API_TYPES.placementApplication, id: placementApplicationId },
      start: startTime,
      end: endTime,
      interviewType: InterviewType.IN_PERSON_INTERVIEW,
      bypassPlacementDocumentRequirementCheck: true,
      bypassInterviewAvailabilityCheck: true,
      notifyInPlacementChatChannel: true,
    });

    await queryClient.invalidateQueries({
      queryKey: [
        getFetchPaginatedPlacementApplicationsQueryKey({
          workplaceId: profile.userId,
          queryParams,
        })[0],
      ],
    });
  };

  return (
    <Dialog fullWidth open={modalState.modalIsOpen} maxWidth="xs" onClose={modalState.closeModal}>
      <DialogTitle>Schedule interview</DialogTitle>
      <DialogContent dividers>
        <FormProvider {...methods}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Date selector */}
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  disablePast
                  label="Interview Date"
                  maxDate={addDays(new Date(), maxDaysIntoFuture)}
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      error: Boolean(errors.date),
                      helperText: errors.date?.message,
                    },
                  }}
                />
              )}
            />

            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Controller
                name="interviewTime.startTime"
                control={control}
                render={({ field }) => (
                  <InterviewTimePickerField
                    value={field.value}
                    label="Interview Start Time"
                    error={Boolean(errors.interviewTime)}
                    onChange={async (value) => {
                      field.onChange(value);
                      // eslint-disable-next-line @typescript-eslint/no-floating-promises
                      trigger("interviewTime");
                    }}
                  />
                )}
              />

              <Controller
                name="interviewTime.endTime"
                control={control}
                render={({ field }) => (
                  <InterviewTimePickerField
                    value={field.value}
                    label="Interivew End Time"
                    error={Boolean(errors.interviewTime)}
                    onChange={async (value) => {
                      field.onChange(value);
                      // eslint-disable-next-line @typescript-eslint/no-floating-promises
                      trigger("interviewTime");
                    }}
                  />
                )}
              />
            </Stack>

            {errors.interviewTime && (
              <FormHelperText error>{errors.interviewTime.message}</FormHelperText>
            )}

            <Controller
              name="acknowledgeCandidateAvailability"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  error={Boolean(errors.acknowledgeCandidateAvailability)}
                >
                  <FormControlLabel
                    control={<Checkbox {...field} />}
                    label="I acknowledge that I am scheduling the interview based on the times agreed upon by the candidate"
                  />
                  {errors.acknowledgeCandidateAvailability && (
                    <FormHelperText error>
                      {errors.acknowledgeCandidateAvailability.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Stack>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <LoadingButton variant="contained" isLoading={isCreating} onClick={handleSubmit(onSubmit)}>
          Schedule interview
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
